import React, { useState, useRef } from 'react'

import { useProcessKey } from '@please/lrud'

import { Direction } from '@/constants/focus'
import { usePageOptionsUpdate } from '@/hooks/usePageOptionsUpdate'
import useStore from '@/store/useStore'

import {
  HomeTabPageTabActiveLine,
  HomeTabPageTabElem,
  HomeTabPageTabsLayout,
} from '../styles'

type Props = {
  items: { title: string }[]
  isFocused: boolean
  defaultTabIndex?: number
  onSelected: (index: number) => void
  focusIdPrefix?: string
  isPopup?: boolean
}

export const HomePageTabs = ({
  items,
  isFocused,
  defaultTabIndex = 0,
  onSelected,
  focusIdPrefix = '',
  isPopup,
}: Props) => {
  const focusId = `${focusIdPrefix}_homePageTabs`
  const processKey = useProcessKey()
  const [defaultFocusChild, setDefaultFocusChild] = useState(defaultTabIndex)
  const [activeTabIndex, setActiveTabIndex] = useState(defaultTabIndex)
  const [isTransitionActive, setIsTransitionActive] = useState(false)
  const { resetPageOptions } = usePageOptionsUpdate(isPopup)

  const setPageSlideIndex = useStore((state) => state.page.setSlideIndex)
  const setPagePopupSlideIndex = useStore((state) => state.popup.setSlideIndex)
  const setSlideIndex = isPopup ? setPagePopupSlideIndex : setPageSlideIndex

  const tabsRefs = useRef<HTMLDivElement[]>([])

  const tabLineStyle = !!tabsRefs.current.length && {
    transform: `translateX(${tabsRefs.current[activeTabIndex].offsetLeft}px)`,
    width: tabsRefs.current[activeTabIndex].offsetWidth,
    opacity: isFocused ? 1 : 0,
  }

  if (!items.length) {
    return null
  }

  const onMove = (e: MoveEvent) => {
    const currentChildIndex =
      e.direction === Direction.Forward
        ? Number(e.prevChildIndex) + 1
        : Number(e.prevChildIndex) - 1
    setSlideIndex(0)
    resetPageOptions()
    setActiveTabIndex(currentChildIndex)
    setDefaultFocusChild(currentChildIndex)
    setIsTransitionActive(true)
  }

  const onTabSelected = (index: number) => {
    if (activeTabIndex !== index) {
      setActiveTabIndex(index)
      setDefaultFocusChild(index)
      setIsTransitionActive(true)
    } else {
      !isTransitionActive && processKey.down()
    }
  }

  return (
    <HomeTabPageTabsLayout
      focusId={focusId}
      orientation="horizontal"
      defaultFocusChild={defaultFocusChild}
      onMove={onMove}
    >
      {items.map((item, index) => {
        return (
          <HomeTabPageTabElem
            ref={(ref: HTMLDivElement) => (tabsRefs.current[index] = ref)}
            id={`${focusIdPrefix}_homePageTab${index}`}
            focusId={`${focusIdPrefix}_homePageTab${index}`}
            onSelected={() => onTabSelected(index)}
            key={`${focusIdPrefix}_homePageTab${index}`}
            className={index === activeTabIndex ? 'isActiveTab' : ''}
          >
            {item?.title || 'DEBUG: NO NAME'}
          </HomeTabPageTabElem>
        )
      })}

      {tabLineStyle && (
        <HomeTabPageTabActiveLine
          style={tabLineStyle}
          onTransitionEnd={() => {
            onSelected(activeTabIndex)
            setIsTransitionActive(false)
          }}
        />
      )}
    </HomeTabPageTabsLayout>
  )
}
